import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useClientData } from '../ClientLayout.jsx';
import DebtorColumnsPicker, { useDebtorListColumns } from '../DebtorColumnsPicker.jsx';
import {
  DEBTOR_LIST_COLUMNS,
  debtorCellValue,
  debtorSortFieldLabel,
  exportDebtorsCsv,
  loadDebtorListSort,
  saveDebtorListSort,
  sortDebtors,
  visibleDebtorColumns,
} from '../../lib/debtorListColumns.js';
import { nextColumnSort, sortStatusLabel } from '../../lib/listColumnSort.js';
import { selectedDebtorsFromIds } from '../../lib/debtorArchive.js';
import ListPagination, { useListPagination } from '../ListPagination.jsx';
import ListSortMark from '../ListSortMark.jsx';

function BulkActionTip({ label, text }) {
  return (
    <span className="list-bulk-tip kanban-stage-tip">
      <button
        type="button"
        className="kanban-stage-tip-btn"
        tabIndex={0}
        aria-label={`${label}: ${text}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden>
          <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path
            d="M8 7.2v3.6M8 5.2h.01"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
      <span className="kanban-stage-tip-bubble list-bulk-tip-bubble" role="tooltip">
        {text}
      </span>
    </span>
  );
}

const BULK_ACTION_TIPS = {
  export: 'Download the selected debtors as a CSV spreadsheet.',
  archive: 'Move selected debtors to Archived. View and restore them anytime from Archived.',
  unarchive: 'Restore selected debtors to the active Debtors list.',
  delete:
    'Permanently delete selected archived debtors. This cannot be undone. Related demands stay on Demands unless archived separately.',
};

function DebtorsTable({
  debtors,
  columns,
  mode = 'active',
  onOpenDebtor,
  onBulkExport,
  onBulkArchive,
  onBulkUnarchive,
  onBulkDelete,
}) {
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [sort, setSort] = useState(() => loadDebtorListSort());
  const bulkMenuRef = useRef(null);
  const fromPath = mode === 'archived' ? '/client/debtors/archived' : '/client/debtors';
  const sortedDebtors = useMemo(() => sortDebtors(debtors, sort), [debtors, sort]);
  const pagination = useListPagination(sortedDebtors, {
    storageKey: mode === 'archived' ? 'debtors-archived' : 'debtors',
  });
  const pageDebtors = pagination.pageItems;
  const sortLabel = sortStatusLabel(sort, debtorSortFieldLabel);

  function toggleHeaderSort(columnId) {
    const col = DEBTOR_LIST_COLUMNS.find((c) => c.id === columnId);
    setSort(saveDebtorListSort(nextColumnSort(sort, columnId, col?.sortType || 'string')));
  }

  useEffect(() => {
    setSelected(new Set());
    setBulkOpen(false);
  }, [debtors, mode, pagination.page, pagination.pageSize, sort.columnId, sort.direction]);

  useEffect(() => {
    if (!selected.size) setBulkOpen(false);
  }, [selected.size]);

  useEffect(() => {
    if (!bulkOpen) return undefined;
    function onDocClick(e) {
      if (bulkMenuRef.current && !bulkMenuRef.current.contains(e.target)) {
        setBulkOpen(false);
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') setBulkOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [bulkOpen]);

  function toggleOne(id, checked) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(String(id));
      else next.delete(String(id));
      return next;
    });
  }

  function toggleSelectAll(checked) {
    if (!checked) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set(pageDebtors.map((d) => String(d.id))));
  }

  function runBulk(action) {
    const rows = selectedDebtorsFromIds(sortedDebtors, selected);
    setBulkOpen(false);
    action?.(rows);
    if (action !== onBulkExport) {
      setSelected(new Set());
    }
  }

  const pageIds = pageDebtors.map((d) => String(d.id));
  const selectedOnPage = pageIds.filter((id) => selected.has(id)).length;
  const allSelected = pageIds.length > 0 && selectedOnPage === pageIds.length;
  const someSelected = selectedOnPage > 0 && selectedOnPage < pageIds.length;

  if (!debtors.length) {
    return (
      <div className="empty-state">
        {mode === 'archived' ? 'No archived debtor records yet.' : 'No debtor records yet.'}
      </div>
    );
  }

  return (
    <div className="list-view debtor-list-view">
      <div className="list-view-toolbar">
        <p className="list-view-count">
          {debtors.length} debtor{debtors.length === 1 ? '' : 's'}
          {selected.size ? ` · ${selected.size} selected` : ''}
          {sortLabel ? ` · ${sortLabel}` : ''}
        </p>
        {selected.size ? (
          <div className="list-bulk-menu" ref={bulkMenuRef}>
            <button
              type="button"
              className="btn-ghost list-bulk-toggle"
              aria-expanded={bulkOpen}
              aria-haspopup="menu"
              onClick={() => setBulkOpen((open) => !open)}
            >
              Bulk Actions
              <span aria-hidden>▾</span>
            </button>
            {bulkOpen ? (
              <div className="list-bulk-dropdown" role="menu" aria-label="Bulk debtor actions">
                <div className="list-bulk-item-row" role="none">
                  <button
                    type="button"
                    role="menuitem"
                    className="list-bulk-item"
                    onClick={() => runBulk(onBulkExport)}
                  >
                    Export
                  </button>
                  <BulkActionTip label="Export" text={BULK_ACTION_TIPS.export} />
                </div>
                {mode === 'archived' ? (
                  <>
                    <div className="list-bulk-item-row" role="none">
                      <button
                        type="button"
                        role="menuitem"
                        className="list-bulk-item"
                        onClick={() => runBulk(onBulkUnarchive)}
                      >
                        Unarchive
                      </button>
                      <BulkActionTip label="Unarchive" text={BULK_ACTION_TIPS.unarchive} />
                    </div>
                    <div className="list-bulk-item-row" role="none">
                      <button
                        type="button"
                        role="menuitem"
                        className="list-bulk-item is-danger"
                        onClick={() => runBulk(onBulkDelete)}
                      >
                        Delete
                      </button>
                      <BulkActionTip label="Delete" text={BULK_ACTION_TIPS.delete} />
                    </div>
                  </>
                ) : (
                  <div className="list-bulk-item-row" role="none">
                    <button
                      type="button"
                      role="menuitem"
                      className="list-bulk-item is-danger"
                      onClick={() => runBulk(onBulkArchive)}
                    >
                      Archive
                    </button>
                    <BulkActionTip label="Archive" text={BULK_ACTION_TIPS.archive} />
                  </div>
                )}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="debtor-table-wrap">
        <table className="matters-table debtor-table">
          <thead>
            <tr>
              <th className="list-col-check">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                  aria-label="Select all debtors"
                />
              </th>
              {columns.map((col) => {
                const active = sort.columnId === col.id;
                return (
                  <th key={col.id}>
                    <button
                      type="button"
                      className={`list-sort-header${active ? ' is-active' : ''}`}
                      onClick={() => toggleHeaderSort(col.id)}
                      aria-label={`Sort by ${col.label} ${
                        active && sort.direction === 'asc' ? 'descending' : 'ascending'
                      }`}
                    >
                      <span className="list-col-label">{col.label}</span>
                      <ListSortMark active={active} direction={sort.direction} />
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pageDebtors.map((d) => {
              const label = debtorCellValue(d, 'debtorName');
              const id = String(d.id);
              const isSelected = selected.has(id);
              return (
                <tr
                  key={d.id}
                  className={`matter-row is-clickable${isSelected ? ' is-selected' : ''}`}
                  onClick={() => onOpenDebtor(d, fromPath)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onOpenDebtor(d, fromPath);
                    }
                  }}
                  tabIndex={0}
                  role="link"
                  aria-label={`Open debtor record for ${label}`}
                >
                  <td
                    className="list-col-check"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => toggleOne(id, e.target.checked)}
                      aria-label={`Select ${label}`}
                    />
                  </td>
                  {columns.map((col) => (
                    <td key={col.id}>
                      {col.id === 'debtorName' ? (
                        <strong className="list-column-link">{debtorCellValue(d, col.id)}</strong>
                      ) : col.id === 'email' && d.email ? (
                        <a
                          className="list-column-link"
                          href={`mailto:${d.email}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {debtorCellValue(d, col.id)}
                        </a>
                      ) : (
                        debtorCellValue(d, col.id)
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ListPagination
        page={pagination.page}
        pageCount={pagination.pageCount}
        pageSize={pagination.pageSize}
        onPageSizeChange={pagination.setPageSize}
        rangeStart={pagination.rangeStart}
        rangeEnd={pagination.rangeEnd}
        canPrev={pagination.canPrev}
        canNext={pagination.canNext}
        onPrev={pagination.goPrev}
        onNext={pagination.goNext}
      />
    </div>
  );
}

export default function DebtorsPage() {
  const { debtors, openDebtor, archiveDebtors, setNotice, notice, archivedDebtors } =
    useClientData();
  const { visibleIds, toggleColumn, resetColumns } = useDebtorListColumns();
  const columns = useMemo(() => visibleDebtorColumns(visibleIds), [visibleIds]);

  function handleBulkArchive(rows) {
    const updated = archiveDebtors(rows);
    setNotice(
      `Archived ${updated.length} debtor${updated.length === 1 ? '' : 's'}. View them under Archived.`
    );
  }

  function handleBulkExport(rows) {
    const count = exportDebtorsCsv(rows);
    setNotice(`Exported ${count} debtor${count === 1 ? '' : 's'} to CSV.`);
  }

  return (
    <div className="client-page">
      <div className="section-head">
        <div>
          <h1>Debtors</h1>
          <p>
            Debtor contact records, including those created before a demand. Customize which
            columns appear by selecting the filter button.
          </p>
        </div>
        <div className="section-head-actions">
          <Link className="btn-ghost" to="/client/debtors/archived">
            Archived
            {archivedDebtors.length ? ` (${archivedDebtors.length})` : ''}
          </Link>
          <DebtorColumnsPicker
            visibleIds={visibleIds}
            onToggle={toggleColumn}
            onReset={resetColumns}
          />
        </div>
      </div>

      {notice ? <p className="inline-notice">{notice}</p> : null}

      {debtors.length ? (
        <DebtorsTable
          debtors={debtors}
          columns={columns}
          mode="active"
          onOpenDebtor={openDebtor}
          onBulkExport={handleBulkExport}
          onBulkArchive={handleBulkArchive}
        />
      ) : (
        <div className="empty-state">No active debtor records yet. Use + to create one.</div>
      )}
    </div>
  );
}

export function DebtorsArchivedPage() {
  const { archivedDebtors, openDebtor, unarchiveDebtors, deleteDebtors, setNotice, notice } =
    useClientData();
  const { visibleIds, toggleColumn, resetColumns } = useDebtorListColumns();
  const columns = useMemo(() => visibleDebtorColumns(visibleIds), [visibleIds]);

  function handleBulkUnarchive(rows) {
    const updated = unarchiveDebtors(rows);
    setNotice(
      `Restored ${updated.length} debtor${updated.length === 1 ? '' : 's'} to the active list.`
    );
  }

  function handleBulkDelete(rows) {
    const count = rows?.length || 0;
    if (!count) return;
    const ok = window.confirm(
      count === 1
        ? 'Permanently delete this archived debtor? This cannot be undone. Related demands stay on Demands.'
        : `Permanently delete ${count} archived debtors? This cannot be undone. Related demands stay on Demands.`
    );
    if (!ok) return;
    const updated = deleteDebtors(rows);
    setNotice(
      `Deleted ${updated.length} debtor${updated.length === 1 ? '' : 's'} permanently.`
    );
  }

  function handleBulkExport(rows) {
    const count = exportDebtorsCsv(
      rows,
      `demandjet-debtors-archived-${new Date().toISOString().slice(0, 10)}.csv`
    );
    setNotice(`Exported ${count} debtor${count === 1 ? '' : 's'} to CSV.`);
  }

  return (
    <div className="client-page">
      <div className="section-head">
        <div>
          <h1>Archived debtors</h1>
          <p>
            Archived debtor contact records. Restore them anytime to the active Debtors list.
            Related demands stay on Demands unless archived separately.
          </p>
        </div>
        <div className="section-head-actions">
          <Link className="btn-ghost" to="/client/debtors">
            Active list
          </Link>
          <DebtorColumnsPicker
            visibleIds={visibleIds}
            onToggle={toggleColumn}
            onReset={resetColumns}
          />
        </div>
      </div>

      {notice ? <p className="inline-notice">{notice}</p> : null}

      {archivedDebtors.length ? (
        <DebtorsTable
          debtors={archivedDebtors}
          columns={columns}
          mode="archived"
          onOpenDebtor={openDebtor}
          onBulkExport={handleBulkExport}
          onBulkUnarchive={handleBulkUnarchive}
          onBulkDelete={handleBulkDelete}
        />
      ) : (
        <div className="empty-state">No archived debtor records yet.</div>
      )}
    </div>
  );
}
